import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import fontesRendaValidationSchema from './validation';
import { ROUTES } from 'routes/constants';
import { CuffaIncomeSourceEnum } from 'interfaces/cuffa.interface';

const fontesEnum = Object.values(CuffaIncomeSourceEnum);
const outroValorFixo = 'Outro';
const fontesPadrao = [...fontesEnum, outroValorFixo];

const incomeSourceToBackendKeyMap: { [key: string]: string } = {
  [CuffaIncomeSourceEnum.PRESTACAO_SERVICO_EXTERNO]: 'prestacao_servico_externo',
  [CuffaIncomeSourceEnum.ASSALARIADO_ATIVIDADE_FORA_PROPRIEDADE]: 'assalariado_atividade_fora_propriedade',
  [CuffaIncomeSourceEnum.ATIVIDADE_PRODUTIVA_PROPRIEDADE]: 'atividade_produtiva_propriedade',
  [CuffaIncomeSourceEnum.APOSENTADORIA]: 'aposentadoria',
  [CuffaIncomeSourceEnum.PENSAO]: 'pensao',
  [CuffaIncomeSourceEnum.RECURSOS_PROGRAMAS_SOCIAIS]: 'recursos_programas_sociais',
};

const backendKeyToIncomeSourceMap = Object.fromEntries(
  Object.entries(incomeSourceToBackendKeyMap).map(([key, value]) => [value, key])
);

export function useFontesRendaFamilia() {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const { salvarDadosParciais } = useCuffaStore();
  const advanceNext = useRef(false);

  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [ordens, setOrdens] = useState<{ value: string; order: number }[]>([]);
  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const formik = useFormik({
    initialValues: { outroTexto: '' },
    validationSchema: fontesRendaValidationSchema,
    onSubmit: async (values) => {
      if (!uuid) {
        setSnackbarMessage("Não é possível salvar sem um UUID válido.");
        setOpenErrorSnackbar(true);
        return;
      }
      if (selecionadas.length === 0) {
        setSnackbarMessage('Selecione pelo menos uma fonte de renda.');
        setOpenErrorSnackbar(true);
        return;
      }
      const ordensDefinidas = ordens.filter((o) => o.order > 0);
      if (ordensDefinidas.length !== selecionadas.length) {
        setSnackbarMessage('Defina uma prioridade para cada fonte de renda selecionada.');
        setOpenErrorSnackbar(true);
        return;
      }
      if (new Set(ordensDefinidas.map((o) => o.order)).size !== selecionadas.length) {
        setSnackbarMessage('As prioridades devem ser únicas para cada fonte.');
        setOpenErrorSnackbar(true);
        return;
      }
      if (selecionadas.includes(outroValorFixo) && values.outroTexto.trim() === '') {
        setSnackbarMessage('Descreva o campo "Outro".');
        setOpenErrorSnackbar(true);
        return;
      }
      setSaving(true);
      const payload: { [key: string]: any } = {};
      ordens.forEach(item => {
        const { value, order } = item;
        if (value === outroValorFixo) {
          payload.outros = [{ priority: order, description: values.outroTexto.toUpperCase() }];
        } else {
          const backendKey = incomeSourceToBackendKeyMap[value];
          if (backendKey) {
            payload[backendKey] = order;
          }
        }
      });

      try {
        await CuffaAPI.updateFamilyIncomeSources(uuid, payload);
        const proximaRota = ROUTES.PARTICIPACAO_FEMININA_CUFFA_INCLUIR.replace(':uuid', uuid);
        salvarDadosParciais({
          fontesRendaFamilia: { selecionadas, ordens, outroTexto: values.outroTexto },
          ultimaRota: proximaRota,
        });
        setSnackbarMessage('Dados salvos com sucesso!');
        setOpenSuccessSnackbar(true);
        if (advanceNext.current) {
          setTimeout(() => navigate(proximaRota), 1000);
        }
      } catch (error: any) {
        console.error('Erro ao salvar fontes de renda:', error);
        setSnackbarMessage(error.message ?? 'Erro ao salvar os dados. Tente novamente.');
        setOpenErrorSnackbar(true);
      } finally {
        setSaving(false);
        advanceNext.current = false;
      }
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!uuid) {
        setSnackbarMessage("UUID da coleta não encontrado. Retorne e inicie novamente.");
        setOpenErrorSnackbar(true);
        setInitialLoading(false);
        return;
      }

      // Primeiro tenta ler do localStorage específico por coleta, depois do global
      try {
        const storageKeys: string[] = [];
        if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
        storageKeys.push('cuffa-storage');

        for (const storageKey of storageKeys) {
          try {
            const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.fontesRendaFamilia;
            if (!saved) continue;

            // Aceita tanto formato salvo pelo front quanto o formato vindo da API
            if (Array.isArray(saved.selecionadas)) {
              setSelecionadas(saved.selecionadas);
            }
            if (Array.isArray(saved.ordens)) {
              setOrdens(saved.ordens);
            }
            if (typeof saved.outroTexto === 'string') {
              formik.setFieldValue('outroTexto', saved.outroTexto);
            }

            setInitialLoading(false);
            return;
          } catch (e) {
            console.warn(`Falha ao parsear ${storageKey} ao restaurar fontesRendaFamilia:`, e);
            continue;
          }
        }
      } catch (e) {
        console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar fontesRendaFamilia', e);
      }

      // Se não encontrou no storage, busca pela API
      try {
        const response = await CuffaAPI.getDataByID(uuid);
        const savedData = response.dados?.income_sources ?? response.dados?.family_income_sources;

        if (savedData) {
          const novasSelecionadas: string[] = [];
          const novasOrdens: { value: string; order: number }[] = [];
          let novoOutroTexto = '';

          for (const backendKey in savedData) {
            if (Object.hasOwn(savedData, backendKey)) {
              if (backendKey === 'outros') {
                const outroData = savedData.outros?.[0];
                if (outroData) {
                  novasSelecionadas.push(outroValorFixo);
                  novasOrdens.push({ value: outroValorFixo, order: outroData.priority });
                  novoOutroTexto = outroData.description;
                }
              } else {
                const sourceName = backendKeyToIncomeSourceMap[backendKey];
                if (sourceName) {
                  novasSelecionadas.push(sourceName);
                  novasOrdens.push({ value: sourceName, order: savedData[backendKey] });
                }
              }
            }
          }
          novasOrdens.sort((a, b) => a.order - b.order);
          setSelecionadas(novasSelecionadas);
          setOrdens(novasOrdens);
          formik.setFieldValue('outroTexto', novoOutroTexto);
        }
      } catch (error) {
        console.error("Erro ao carregar dados de fontes de renda:", error);
        setSnackbarMessage("Erro ao carregar dados. Tente novamente.");
        setOpenErrorSnackbar(true);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [uuid]);

  const handleCloseSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpenSuccessSnackbar(false);
    setOpenErrorSnackbar(false);
  };

  type Ordem = { value: string; order: number };

  const handleSelecionar = (nome: string) => {
    const isChecked = selecionadas.includes(nome);

    let novasSelecionadas: string[];
    let novasOrdens: Ordem[];

    if (isChecked) {
      novasSelecionadas = selecionadas.filter((c: string) => c !== nome);
      const ordensRestantes: Ordem[] = ordens.filter((o: Ordem) => o.value !== nome);
      const ordensOrdenadas: Ordem[] = [...ordensRestantes].sort((a: Ordem, b: Ordem) => a.order - b.order);

      novasOrdens = ordensOrdenadas.map((item: Ordem, index: number) => ({
        ...item,
        order: index + 1,
      }));

      if (nome === outroValorFixo) {
        formik.setFieldValue('outroTexto', '');
      }
    } else {
      novasSelecionadas = [...selecionadas, nome];
      const novaOrdem: Ordem = { value: nome, order: selecionadas.length + 1 };
      novasOrdens = [...ordens, novaOrdem];
    }

    setSelecionadas(novasSelecionadas);
    setOrdens(novasOrdens);
  };

  const handleOrderChange = (value: string, novaOrdem: number) => {
    setOrdens((prevOrdens) => {
      const ordensCopia = prevOrdens.map(o => ({ ...o }));
      const itemAtualIndex = ordensCopia.findIndex((o) => o.value === value);
      const itemAlvoIndex = ordensCopia.findIndex((o) => o.order === novaOrdem);

      if (itemAlvoIndex > -1) {
        ordensCopia[itemAlvoIndex].order = ordensCopia[itemAtualIndex].order;
      }
      ordensCopia[itemAtualIndex].order = novaOrdem;

      return ordensCopia;
    });
  };

  const handleSave = async () => {
    advanceNext.current = false;
    await formik.submitForm();
  };

  const handleAdvance = async () => {
    advanceNext.current = true;
    await formik.submitForm();
  };

  return {
    formik,
    initialLoading,
    saving,
    selecionadas,
    ordens,
    openSuccessSnackbar,
    openErrorSnackbar,
    snackbarMessage,
    handleCloseSnackbar,
    handleSelecionar,
    handleOrderChange,
    handleSave,
    handleAdvance,
    advanceNext,
  } as const;
}

export default useFontesRendaFamilia;
